import { encodeAbiParameters, keccak256 } from "viem";

import { tickToSqrtPriceX96 } from "../../lib/constants";

import type { Address, Hex } from "viem";

// ---------------------------------------------------------------------------
// V3 Position Type
// ---------------------------------------------------------------------------

export interface V3Position {
  tokenId: bigint;
  token0: Address;
  token1: Address;
  fee: number;
  tickLower: number;
  tickUpper: number;
  liquidity: bigint;
  feeGrowthInside0LastX128: bigint;
  feeGrowthInside1LastX128: bigint;
  tokensOwed0: bigint;
  tokensOwed1: bigint;
}

// ---------------------------------------------------------------------------
// V4 Position Type
// ---------------------------------------------------------------------------

export interface V4Position {
  id: string;
  currency0: Address;
  currency1: Address;
  fee: number;
  tickSpacing: number;
  hooks: Address;
  tickLower: number;
  tickUpper: number;
  salt: Hex;
  liquidity: bigint;
  feeGrowthInside0LastX128: bigint;
  feeGrowthInside1LastX128: bigint;
}

export interface V4PositionDescriptor {
  currency0: Address;
  currency1: Address;
  fee: number;
  tickSpacing: number;
  hooks: Address;
  tickLower: number;
  tickUpper: number;
  salt: Hex;
}

export function computeV4PoolId(
  c0: Address,
  c1: Address,
  fee: number,
  tickSpacing: number,
  hooks: Address,
): Hex {
  return keccak256(
    encodeAbiParameters(
      [
        { type: "address" },
        { type: "address" },
        { type: "uint24" },
        { type: "int24" },
        { type: "address" },
      ],
      [c0, c1, fee, tickSpacing, hooks],
    ),
  );
}

// ---------------------------------------------------------------------------
// V2 Position Type
// ---------------------------------------------------------------------------

export interface V2Position {
  pair: string;
  pairAddress: Address;
  token0: Address;
  token1: Address;
  reserve0: bigint;
  reserve1: bigint;
  lpBalance: bigint;
  totalSupply: bigint;
  userShare0: bigint;
  userShare1: bigint;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Max uint128 for collect() */
export const MAX_UINT128 = 2n ** 128n - 1n;

export const MIN_TICK = -887220;
export const MAX_TICK = 887220;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert a Uniswap tick to a human-readable price string. */
export function tickToPrice(
  tick: number,
  decimals0: number,
  decimals1: number,
): string {
  if (tick <= MIN_TICK) return "0";
  if (tick >= MAX_TICK) return "\u221e";
  const price = Math.pow(1.0001, tick) * Math.pow(10, decimals0 - decimals1);
  if (price >= 1e15) return price.toExponential(2);
  if (price >= 1e12) return `${(price / 1e12).toFixed(2)}T`;
  if (price >= 1e9) return `${(price / 1e9).toFixed(2)}B`;
  if (price >= 1e6) return `${(price / 1e6).toFixed(2)}M`;
  if (price >= 1)
    return price.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  if (price < 1e-8) return "\u2248 0";
  return price.toPrecision(4);
}

/**
 * Format a tick as a human-readable price with token labels.
 * Returns e.g. "2,000.00 USDC/WETH"
 */
export function formatTickPrice(
  tick: number,
  sym0: string,
  sym1: string,
  dec0: number,
  dec1: number,
): string {
  const price = tickToPrice(tick, dec0, dec1);
  return `${price} ${sym1} per ${sym0}`;
}

/**
 * Convert a human-readable price to the nearest Uniswap tick.
 * Inverse of tickToPrice: tick = log(price / 10^(dec0-dec1)) / log(1.0001)
 */
export function priceToTick(price: number, dec0: number, dec1: number): number {
  if (price <= 0) return MIN_TICK;
  const adjusted = price / Math.pow(10, dec0 - dec1);
  const tick = Math.round(Math.log(adjusted) / Math.log(1.0001));
  return Math.max(MIN_TICK, Math.min(MAX_TICK, tick));
}

/** Resolve a token address to its symbol using the deployment token map. */
export function resolveSymbol(
  addr: Address,
  tokens: Record<string, Address>,
): string {
  for (const [sym, tokenAddr] of Object.entries(tokens)) {
    if (tokenAddr.toLowerCase() === addr.toLowerCase()) {
      return sym.toUpperCase();
    }
  }
  return addr.slice(0, 8) + "...";
}

// ---------------------------------------------------------------------------
// Concentrated Liquidity Amount Computation
// ---------------------------------------------------------------------------

const Q96_F = Number(2n ** 96n);

/**
 * Compute token0 and token1 amounts from a concentrated liquidity position.
 * Uses Uniswap V3 math: L * (sqrt(upper) - sqrt(current)) / (sqrt(current) * sqrt(upper)) for token0,
 * and L * (sqrt(current) - sqrt(lower)) for token1.
 */
export function computePositionAmounts(
  liquidity: bigint,
  tickLower: number,
  tickUpper: number,
  currentTick: number,
  decimals0: number,
  decimals1: number,
): { amount0: number; amount1: number } {
  const sqrtLower = Number(tickToSqrtPriceX96(tickLower)) / Q96_F;
  const sqrtUpper = Number(tickToSqrtPriceX96(tickUpper)) / Q96_F;
  const L = Number(liquidity);

  let amount0Raw = 0;
  let amount1Raw = 0;

  if (currentTick < tickLower) {
    // All token0
    amount0Raw = L * (1 / sqrtLower - 1 / sqrtUpper);
  } else if (currentTick >= tickUpper) {
    // All token1
    amount1Raw = L * (sqrtUpper - sqrtLower);
  } else {
    // In range — split
    const sqrtCurrent = Number(tickToSqrtPriceX96(currentTick)) / Q96_F;
    amount0Raw = L * (1 / sqrtCurrent - 1 / sqrtUpper);
    amount1Raw = L * (sqrtCurrent - sqrtLower);
  }

  return {
    amount0: amount0Raw / 10 ** decimals0,
    amount1: amount1Raw / 10 ** decimals1,
  };
}
